import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getRatingIntFromText } from '../../../../models/UiUtils';
import { useGetGoogleBusinessMetadata, useGetResourceSelector, useInfiniteReviews } from '../../../../queries';
import { formatLocation } from '../../../../utils/googleBusinessUtils';
import googleLogo from '../../../assets/google_logo.png';
import { nameCircles, noteTintColor, success, successTint } from '../../../styles/colors';
import { baseStyleSheet, bs } from '../../../styles/styles';
import { GoogleBusinessReplyModalControlWrapper } from '../../googleBusinessProfiles/ReplyModal/GoogleBusinessReplyModalControlWrapper';
import { StarRating } from '../../surveys/StarRating';
import { GoogleBusinessProfileConnectedIntegrationGraphic } from '../../svgs/graphics/GoogleBusinessProfileConnectedIntegrationGraphic';
import { GoogleBusinessProfileNoReviews } from '../../svgs/graphics/GoogleBusinessProfileNoReviews';
import { SliderArrowLeft } from '../../svgs/icons/SliderArrowLeft';
import { SliderArrowRight } from '../../svgs/icons/SliderArrowRight';
import { SuccessCheckIcon } from '../../svgs/icons/SuccessCheckIcon';
import { DashboardCard, DashboardCardLabel, DashboardCardStat, DashboardCardTag } from '../DashboardCard';
import { ReviewActions } from './ReviewActions';
import { styleSheet } from './styles';

export type ActiveGoogleBusinessReview = Api.IGoogleBusinessReview & { includeGeneratedReply?: boolean };

interface DashboardGoogleBusinessProfileCardProps {
	queryCommand: Api.IQueryStringCommand;
}

// Extracted component for the header section
const CardHeader = ({ metadata }: { metadata: Api.IGoogleBusinessMetadata }) => (
	<header className={css(styleSheet.cardHeader)}>
		<div className={css(styleSheet.headerColumns)}>
			<figure className={css(styleSheet.logoWrapper)}>
				<img className={css(styleSheet.logo)} src={googleLogo} alt='Google Logo' />
				<figcaption>Business Profile</figcaption>
			</figure>
			<div className={css(styleSheet.ratingsTitle)}>
				<span>{metadata.averageRating}</span>
				<StarRating
					fillColor={noteTintColor}
					total={5}
					value={metadata.averageRating}
					starStyles={[styleSheet.ratingStar]}
					readonly
				/>
				<span className={css(bs.textXs)}>({metadata.totalReviews})</span>
			</div>
		</div>
	</header>
);

// Extracted component for empty state
const EmptyState = () => (
	<DashboardCard styleDeclaration={styleSheet.root}>
		<CardHeader
			metadata={{
				averageRating: 0,
				totalReviews: 0,
				connectionState: Api.GoogleBusinessConnectionState.Connected,
				percentReviewsIncreased: 0,
			}}
		/>
		<section className={css(styleSheet.bodyFull)}>
			<article>
				<h2>Let&apos;s get started</h2>
				<Link to='/surveys' className={css(baseStyleSheet.ctaButtonReverse, styleSheet.getMoreReviews)}>
					Get more reviews
				</Link>
			</article>
			<figure className={css(styleSheet.noReviewsImage)}>
				<GoogleBusinessProfileNoReviews />
			</figure>
		</section>
	</DashboardCard>
);

export const DashboardGoogleBusinessProfileCard = ({ queryCommand }: DashboardGoogleBusinessProfileCardProps) => {
	const [visibleReviewIndex, setVisibleReviewIndex] = useState(0);
	const [activeReview, setActiveReview] = useState<ActiveGoogleBusinessReview>(null);
	const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
	const { data: metadata, status: metadataStatus } = useGetGoogleBusinessMetadata({});
	const isConnected = metadata?.connectionState === Api.GoogleBusinessConnectionState.Connected;
	const userSession = useUserSession();

	const {
		data: reviewData,
		status: reviewStatus,
		fetchNextPage,
	} = useInfiniteReviews({
		enabled: isConnected,
		refetchOnWindowFocus: false,
	});

	const recentReviews = useMemo(
		() => (reviewData?.pages ? reviewData.pages.map(page => page.values).flat() : []),
		[reviewData?.pages]
	);

	useEffect(() => {
		const review = recentReviews.length ? recentReviews[visibleReviewIndex] : null;
		if (!hasOpenedRef.current) {
			setActiveReview(review ? { ...review, includeGeneratedReply: false } : null);
		}
	}, [visibleReviewIndex, recentReviews]);

	const hasOpenedRef = useRef<boolean>();

	useEffect(() => {
		const fetchAndShowReviewFromQuery = async () => {
			if (queryCommandIsGoogleReviewCommand(queryCommand) && !hasOpenedRef.current) {
				hasOpenedRef.current = true;
				try {
					if (queryCommand.reviewId) {
						let review = recentReviews.find(googleReview => googleReview.id === queryCommand.reviewId);
						if (!review) {
							review = await userSession.webServiceHelper.callAsync<Api.IGoogleBusinessReview>(
								`googleBusiness/review/${encodeURIComponent(queryCommand.reviewId)}`,
								'GET'
							);
						}
						if (review.reply) {
							return;
						}
						setActiveReview({
							...review,
							includeGeneratedReply: queryCommand.includeGeneratedReply,
						});
					}
				} catch {
					console.error('Unable to fetch google review from queryCommand');
				} finally {
					setIsReplyModalOpen(true);
					history.pushState({}, '', location.pathname);
				}
			}
		};
		fetchAndShowReviewFromQuery();
	}, [queryCommand, recentReviews, userSession.webServiceHelper]);

	const { data, status } = useGetResourceSelector({
		enabled: metadataStatus === 'success' && isConnected,
		resourceName: 'GoogleBusinessReviews',
	});

	const isLoaded = status === 'success';

	if (!isLoaded || (isLoaded && !isConnected)) {
		return null;
	}

	if (!recentReviews.length) {
		return <EmptyState />;
	}

	const handleNextClick = () => {
		const isOneAwayFromNeedingMoreData = visibleReviewIndex + 2 === recentReviews.length;
		const isNotFullyLoaded = recentReviews.length !== metadata.totalReviews;
		const isNotAlreadyLoading = reviewStatus !== 'pending';

		if (isOneAwayFromNeedingMoreData && isNotFullyLoaded && isNotAlreadyLoading) {
			fetchNextPage();
		}

		setVisibleReviewIndex(visibleReviewIndex + 1);
	};

	const handleBackClick = () => {
		setVisibleReviewIndex(visibleReviewIndex - 1);
	};

	const rating = getRatingIntFromText(activeReview?.rating);
	const showStats = data[0]?.percentReviewsIncreased > 0;

	return (
		<DashboardCard styleDeclaration={styleSheet.root}>
			<CardHeader metadata={metadata} />
			<article className={css(styleSheet.bodyColumns)}>
				{!showStats ? (
					<figure className={css(styleSheet.bodyLeft)}>
						<GoogleBusinessProfileConnectedIntegrationGraphic />
						<figcaption>Let&apos;s get started</figcaption>
						<Link to='/surveys'>Get more reviews</Link>
					</figure>
				) : null}
				{showStats ? (
					<div className={css(styleSheet.bodyLeft)}>
						<DashboardCardTag styleDeclaration={[styleSheet.redBadge, styleSheet.wideBadge]}>
							Since Using Levitate
						</DashboardCardTag>
						<div className={css(styleSheet.percentIncreaseContainer)}>
							<DashboardCardStat styleDeclaration={styleSheet.percentIncreaseStat}>
								<span className={css(styleSheet.percentIncreaseTitle)}>+ {data[0].percentReviewsIncreased}%</span>
								<span className={css(styleSheet.percentIncreaseSubtitle)}>Increase in reviews</span>
							</DashboardCardStat>
						</div>
						<Link to='/surveys'>Get more reviews</Link>
					</div>
				) : null}
				<section className={css(styleSheet.bodyRight)}>
					<div className={css(styleSheet.goBackButton)}>
						<button onClick={handleBackClick} disabled={visibleReviewIndex === 0}>
							<SliderArrowLeft fillColor={nameCircles} />
						</button>
					</div>
					{activeReview ? (
						<div className={css(styleSheet.reviewContainerWrapper)}>
							<div className={css(styleSheet.reviewContainer)}>
								<div className={css(styleSheet.reviewTagsContainer)}>
									<span className={css(styleSheet.author)}>{activeReview.author}</span>
									{activeReview.reply ? (
										<DashboardCardLabel key='responded' styleDeclaration={styleSheet.grayBadge}>
											<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
												<SuccessCheckIcon fillColor={success} ringColor={successTint} />
												<span>{`Responded ${moment(activeReview.replyTime).format('MM/DD/YYYY')}`}</span>
											</div>
										</DashboardCardLabel>
									) : null}
								</div>
								<div className={css(styleSheet.rating)}>
									<StarRating
										fillColor={noteTintColor}
										total={5}
										value={rating}
										starStyles={[styleSheet.ratingStar]}
										readonly
									/>
									<span className={css(styleSheet.daysAgo)}>{moment(activeReview.updateTime).fromNow()}</span>
									{formatLocation(activeReview) && (
										<span className={css(styleSheet.location)}>
											{' '}
											-{'\u00A0\u00A0'}
											{formatLocation(activeReview)}
										</span>
									)}
								</div>
								<p className={css(styleSheet.reviewText)}>{activeReview.text}</p>
							</div>
							<ReviewActions
								activeReview={activeReview}
								userIsAdmin={userSession.isAdmin}
								onPostResponse={() => setIsReplyModalOpen(true)}
								hasReply={!!activeReview.reply}
							/>
						</div>
					) : null}
					<div className={css(styleSheet.goNextButton)}>
						<button onClick={handleNextClick} disabled={visibleReviewIndex + 1 === metadata.totalReviews}>
							<SliderArrowRight fillColor={nameCircles} />
						</button>
					</div>
				</section>
			</article>
			<GoogleBusinessReplyModalControlWrapper
				isOpen={isReplyModalOpen}
				activeReview={activeReview}
				setIsOpen={setIsReplyModalOpen}
			/>
		</DashboardCard>
	);
};

const queryCommandIsGoogleReviewCommand = (
	queryCommand?: Api.IQueryStringCommand | Api.GoogleBusinessReviewsCommand
): queryCommand is Api.GoogleBusinessReviewsCommand => queryCommand?.command === Api.googleBusinessReviewsCommandString;
